import React, { useCallback, useEffect, useRef, useState } from "react";
import i18next from "i18next";
import classNames from "classnames";
import { WidgetToolbar } from "../Widget/WidgetToolbar";
import { WidgetToolbarButton } from "../Widget/WidgetToolbarButton";
import { useAppContext } from "../../Contexts/AppContext";
import { Overlay } from "ol";
import { getMap } from "../GeoLiteMap";
import { useConvertContext } from "../../Contexts/ConvertContext";
import { CoordSystemSelect } from "./CoordSystemSelect";
import { CoordinateInput } from "./CoordinateInput";
import { useMapContext } from "../../Contexts/MapContext";
import { zoomToCoordinates } from "../mapFunctions";
import { ShareLocation } from "./ShareLocation";
import { WidgetWarning } from "./WidgetWarning";
import "./ConvertWidget.scss";

export const ConvertWidget = (): JSX.Element => {
    const { mapClick, setShowToast } = useAppContext();
    const { placingPin, setPlacing, setIdentifyCoordinates } = useMapContext();
    const {
        coordinates,
        setCoordinates,
        coordinatesString,
        pinPlaced,
        setPinPlaced,
        coordinatesInvalid,
        setCoordinatesInvalid
    } = useConvertContext();

    const [sharingActive, setSharingActive] = useState<boolean>(false);
    const [nothingIdentified, setNothingIdentified] = useState<boolean>(false);

    const pinRef = useRef<Overlay>();
    const widgetRef = useRef<HTMLDivElement>();

    useEffect(() => {
        return () => {
            pinRef.current && getMap().removeOverlay(pinRef.current);
        };
    }, []);

    useEffect(() => {
        if (placingPin) {
            pinRef.current = new Overlay({
                element: createPin(),
                offset: [0, -12],
                positioning: "bottom-center"
            });
            getMap().addOverlay(pinRef.current);
            setPinPlaced(true);
            return;
        }
        pinRef.current && getMap().removeOverlay(pinRef.current);
    }, [placingPin]);

    useEffect(() => {
        mapClick && placingPin && setCoordinates(getMap().getEventCoordinate(mapClick.originalEvent));
        mapClick && placingPin && setPinPlaced(true);
    }, [mapClick]);

    const onPlace = useCallback(() => {
        setPlacing(!placingPin);
    }, [placingPin]);

    useEffect(() => {
        coordinates ? pinRef.current && pinRef.current.setPosition(coordinates) : setPinPlaced(false);
        setNothingIdentified(false);
    }, [coordinates]);

    const onClear = () => {
        setPlacing(false);
        setPinPlaced(false);
        setSharingActive(false);
        setCoordinates(undefined);
    };

    const onIdentify = () => {
        document.addEventListener("noIdentifyResults", () => setNothingIdentified(true), { once: true });
        setIdentifyCoordinates({ timestamp: Math.random() * 1234, coordinates });
    };

    const onZoom = () => coordinates && zoomToCoordinates(coordinates);

    const onCopy = useCallback(() => {
        const element = widgetRef.current && widgetRef.current.getBoundingClientRect();
        if (!element) {
            return;
        }

        navigator.clipboard.writeText(coordinatesString);
        setShowToast({
            position: [element.x + 53 - coordinatesString.length, element.y + 98],
            title: i18next.t("coordinateConversion.copySuccess"),
            text: coordinatesString,
            classname: "green-toast"
        });
    }, [coordinatesString]);

    const onShare = useCallback(() => {
        setSharingActive(!sharingActive);
    }, [sharingActive, coordinates]);

    return (
        <>
            <div className="scrollable-content-container" ref={widgetRef}>
                <div className="scrollable-content">
                    <div className="widget-popup-header">
                        <div>{i18next.t("coordinates.coordinateConversion")}</div>
                    </div>
                    <WidgetToolbar>
                        <WidgetToolbarButton
                            iconClass="icon-geolite-map-pin"
                            buttonClass={placingPin && "active"}
                            title={i18next.t("coordinateConversion.track")}
                            onClick={onPlace}
                        />
                        {pinPlaced ? (
                            <>
                                <WidgetToolbarButton
                                    iconClass="icon-geolite-trash"
                                    title={i18next.t("coordinateConversion.clear")}
                                    onClick={onClear}
                                />
                                <WidgetToolbarButton
                                    iconClass="icon-geolite-copy"
                                    buttonClass={classNames({ disabled: !coordinates?.length })}
                                    title={i18next.t("coordinateConversion.copy")}
                                    onClick={onCopy}
                                />
                                <WidgetToolbarButton
                                    iconClass="icon-geolite-convert"
                                    buttonClass={classNames({ disabled: !coordinates?.length, active: sharingActive })}
                                    title={i18next.t("coordinateConversion.share")}
                                    onClick={onShare}
                                />
                                <WidgetToolbarButton
                                    iconClass="icon-geolite-search"
                                    buttonClass={classNames({ disabled: !coordinates?.length })}
                                    title={i18next.t("coordinateConversion.identify")}
                                    onClick={onIdentify}
                                />
                                <WidgetToolbarButton
                                    iconClass="icon-geolite-zoom"
                                    buttonClass={classNames({ disabled: !coordinates?.length })}
                                    title={i18next.t("coordinateConversion.center")}
                                    onClick={onZoom}
                                />
                            </>
                        ) : null}
                    </WidgetToolbar>
                    {sharingActive ? <ShareLocation /> : null}
                    {coordinatesInvalid ? (
                        <WidgetWarning
                            text={i18next.t("coordinateConversion.noCoords")}
                            onClose={() => setCoordinatesInvalid(false)}
                        />
                    ) : null}
                    {nothingIdentified ? (
                        <WidgetWarning
                            text={i18next.t("coordinateConversion.nothingFound")}
                            onClose={() => setNothingIdentified(false)}
                        />
                    ) : null}
                    <CoordinateInput />
                    <CoordSystemSelect />
                </div>
            </div>
        </>
    );
};

const createPin = () => {
    const element = document.createElement("span");
    element.id = "convert-marker";
    element.classList.add("icon-geolite-map-pin");
    const shadow = document.createElement("span");
    shadow.classList.add("icon-geolite-map-pin");
    element.append(shadow);

    return element;
};
